import { Box, Typography, useMediaQuery } from '@mui/material';
import { FC, Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { SignAgreementsDto } from 'src/api/basketService';
import { CreateBookingNoAdvanceFeeDto } from 'src/api/createBookingNoAdvanceFee/dto/CreateBookingNoAdvanceFee.dto';
import { DiscountBanner } from 'src/components/discount-banner';
import { CustomSnackbar } from 'src/components/snackbar/snackbar';
import { routes } from 'src/constants/routes';
import { PaymentPlan, PaymentPlanMobileOverview } from 'src/features/payment-plan';
import { RoomCard, RoomCardMobileOverview } from 'src/features/room-card';
import { StepList } from 'src/features/step-list';
import { StepEnum } from 'src/features/step-list/constants';
import { YourBookingDetails } from 'src/features/your-details/booking-details';
import { UserForm } from 'src/features/your-details/user-form';
import { LoggedInBanner } from 'src/features/your-details/user-form/logged-in-banner';
import { usePostCreateBookingNoAdvanceFee } from 'src/lib/hooks/use-post-create-booking-no-advance-fee';
import { usePostSagepayProcess } from 'src/lib/hooks/use-post-sagepay-process';
import { usePostSignAgreements } from 'src/lib/hooks/use-post-sign-agreements';
import { Currency } from 'src/shared/enum/Currency';
import { ErrorCode } from 'src/shared/enum/ErrorCode';
import { ErrorMessage } from 'src/shared/enum/ErrorMessage';
import { PathNames } from 'src/shared/enum/PathNames';
import { ValidationErrors } from 'src/shared/enum/ValidationErrors';
import {
    clearBasket,
    selectBasket,
    selectBasketId,
    setBasket,
    selectBasketProducts,
} from 'src/store/slices/basket-slice';
import {
    selectBookingDetails,
    selectRoom,
    setBookingRecordId,
} from 'src/store/slices/booking-slice';
import {
    selectCreatedPaymentPlan,
    selectHasAdvancePayment,
    selectPromotion,
    clearPaymentPlan,
} from 'src/store/slices/payment-plan-slice';
import {
    clearSnackbar,
    selectSnackbarState,
    setSnackbarState,
} from 'src/store/slices/snackbar-slice';
import { selectUserDetails, setIsOccupantFlow } from 'src/store/slices/user-slice';
import { AppDispatch } from 'src/store/store';
import { theme } from 'src/styles/theme';
import {
    getStorage,
    setNavigationState,
    setBasketStorage,
} from 'src/utils/local-storage-versioning';

import { AgreementsPage } from '../agreements';
import { RoommatesPage } from '../multi-occupancy';

import { styles } from './styles';

interface BookingDetailsPageProps {
    occupantFlow?: boolean;
}

export const BookingDetailsPage: FC<BookingDetailsPageProps> = ({ occupantFlow = false }) => {
    const [activeStep, setActiveStep] = useState<StepEnum>(StepEnum.YourDetails);
    const [showDetailsForm, setShowDetailsForm] = useState<boolean>(occupantFlow);
    const [hasDiscount, setHasDiscount] = useState<boolean>(false);
    const [sagepayError, setSagepayError] = useState<Error | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const navigate = useNavigate();
    const selectedUser = useSelector(selectUserDetails);
    const selectedPaymentPlan = useSelector(selectCreatedPaymentPlan);
    const basketId = useSelector(selectBasketId);
    const hasAdvancePaymentFee = useSelector(selectHasAdvancePayment);

    const { mutateAsync: postSagepayProcessMutateAsync } = usePostSagepayProcess();
    const { mutateAsync: postSignAgreementsMutateAsync } = usePostSignAgreements();
    const { mutateAsync: postVerifyBookingMutateAsync } = usePostCreateBookingNoAdvanceFee();
    const bookingDetails = useSelector(selectBookingDetails);

    const localStorage = getStorage();
    const localStorageStep = localStorage.navigationState.step;

    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
    const dispatch = useDispatch<AppDispatch>();

    dispatch(setIsOccupantFlow(occupantFlow));

    const { t } = useTranslation();
    const promotion = useSelector(selectPromotion);
    const snackbarState = useSelector(selectSnackbarState);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [snackbarState.open]);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        if (activeStep !== (localStorageStep as StepEnum)) {
            setActiveStep(localStorageStep);
        }
        setNavigationState(PathNames.BOOKING_DETAILS, localStorageStep);
    }, []);

    useEffect(() => {
        if (promotion) {
            setHasDiscount(true);
        }
    }, [promotion]);

    const roomData = useSelector(selectRoom);
    const basketProducts = useSelector(selectBasketProducts);
    const basket = useSelector(selectBasket);

    const hasRoommates = basketProducts ? basketProducts[0].selected_students > 1 : false;

    if (!roomData || !bookingDetails || !basketProducts) return null;
    const currency = bookingDetails.currency as Currency;

    const standardFlowSteps = hasRoommates
        ? [StepEnum.YourDetails, StepEnum.Roommates, StepEnum.Agreements, StepEnum.Payment]
        : [StepEnum.YourDetails, StepEnum.Agreements, StepEnum.Payment];

    const occupantFlowSteps = [StepEnum.YourDetails, StepEnum.Agreements];

    const steps = occupantFlow ? occupantFlowSteps : standardFlowSteps;

    const isAgreementsPage = activeStep === StepEnum.Agreements;

    const handleShowDetailsForm = () => {
        setShowDetailsForm(true);
    };

    const handleHideDetailsForm = () => {
        setShowDetailsForm(false);
    };

    const handleGoToYourDetails = () => {
        setNavigationState(PathNames.BOOKING_DETAILS, StepEnum.YourDetails);
        setActiveStep(StepEnum.YourDetails);
    };

    const handleGoToAgreements = () => {
        setNavigationState(PathNames.BOOKING_DETAILS, StepEnum.Agreements);
        setActiveStep(StepEnum.Agreements);
        window.scrollTo(0, 0);
    };

    const handleGoBack = () => {
        const nextStep = hasRoommates ? StepEnum.Roommates : StepEnum.YourDetails;
        setNavigationState(PathNames.BOOKING_DETAILS, nextStep);
        setActiveStep(nextStep);
    };

    const handleGoToNextStep = () => {
        const nextStep = hasRoommates ? StepEnum.Roommates : StepEnum.Agreements;
        setNavigationState(PathNames.BOOKING_DETAILS, nextStep);
        setActiveStep(nextStep);
        window.scrollTo(0, 0);
    };

    const handleSignAgreementsAndProgress = async (agreementsData: SignAgreementsDto) => {
        setLoading(true);

        if (!selectedUser) {
            throw new Error(ErrorMessage.NO_USER_SELECTED);
        }

        if (!selectedPaymentPlan) {
            throw new Error(ErrorMessage.NO_PAYMENT_PLAN_PROVIDED);
        }

        if (!basketId) {
            throw new Error(ErrorMessage.NO_BOOKING_DETAILS_PROVIDED);
        }

        try {
            if (basket.updated_date) {
                const response = await postSignAgreementsMutateAsync({
                    basketId,
                    agreementsData,
                    updatedAt: basket.updated_date,
                });
                dispatch(setBasket(response));
                setBasketStorage(response);
            }

            if (!hasAdvancePaymentFee) {
                const data = new CreateBookingNoAdvanceFeeDto({ basket_id: basketId });
                const bookingRecordId = await postVerifyBookingMutateAsync(data);

                if (bookingRecordId) {
                    dispatch(setBookingRecordId(bookingRecordId));
                    navigate(PathNames.PROCESSING);
                    return;
                }
            }
            // After successful agreement signing, call the Sagepay process
            const response = await postSagepayProcessMutateAsync(basketId);

            if (response?.redirect_url) {
                window.location.href = response.redirect_url;
            }
        } catch (error) {
            if (error.response.data.code === ErrorCode.ERROR_BASKET_OUTDATED) {
                dispatch(clearBasket());
                dispatch(setSnackbarState({ message: t('basket_out_of_date_message') }));
                navigate(routes.summary.index);
                return;
            }
            if (error.response.data.code === ErrorCode.NO_CODES_LEFT) {
                dispatch(clearBasket());
                dispatch(setSnackbarState({ message: t('no_codes_left_message') }));
                navigate(routes.summary.index);
                return;
            }
            if (error.response.data.code === ErrorCode.PROMOTION_NOT_FOUND) {
                dispatch(clearBasket());
                dispatch(setSnackbarState({ message: t('promotion_not_found_message') }));
                navigate(routes.summary.index);
                return;
            }
            if (error.response.data.code === ErrorCode.PROMOTION_EXPIRED) {
                dispatch(clearBasket());
                dispatch(clearPaymentPlan());
                dispatch(setSnackbarState({ message: t('promotion_expired_message') }));
                navigate(routes.summary.index);
                return;
            }
            if (error.response.data.code === ErrorCode.REBOOKER_STATUS_CHANGED) {
                dispatch(clearBasket());
                dispatch(setSnackbarState({ message: t('rebooker_status_changed_message') }));
                navigate(routes.summary.index);
                return;
            }
            if (error.response.data && error.response.data.data && error.response.data.data.room) {
                if (error.response.data.data.room[0] === ValidationErrors.ROOM_NOT_AVAILABLE) {
                    navigate(routes.roomNotAvailable.index);
                    return;
                }
                if (error.response.data.data.room[0] === ValidationErrors.BASE_PRICE_MISMATCH) {
                    navigate(routes.summary.index);
                    dispatch(setSnackbarState({ message: t('room_price_updated_message') }));
                    return;
                }
            }
            setSagepayError(error);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateNavigationStorage = () => {
        const nextStep = hasRoommates ? StepEnum.Roommates : StepEnum.Agreements;
        setNavigationState(PathNames.BOOKING_DETAILS, nextStep);
    };

    return (
        <Box css={styles.layout}>
            <CustomSnackbar
                open={snackbarState.open}
                message={snackbarState.message}
                onClose={() => dispatch(clearSnackbar())}
                duration={8}
                isSmallScreen={isSmallScreen}
            />
            <Box css={styles.leftSide}>
                <StepList activeStep={activeStep} steps={steps} />
                {activeStep === StepEnum.YourDetails && (
                    <Fragment>
                        {!occupantFlow && <LoggedInBanner />}
                        <RoomCardMobileOverview />
                        <YourBookingDetails />
                        {!occupantFlow && (
                            <PaymentPlanMobileOverview
                                hasDiscount={hasDiscount}
                                currency={currency}
                            />
                        )}
                        <UserForm
                            onFormSubmit={handleGoToNextStep}
                            showDetailsForm={showDetailsForm}
                            handleShowDetailsForm={handleShowDetailsForm}
                            handleHideDetailsForm={handleHideDetailsForm}
                            hasRoommates={hasRoommates}
                            handleUpdateNavigationStorage={handleUpdateNavigationStorage}
                        />
                    </Fragment>
                )}

                {activeStep === StepEnum.Roommates && hasRoommates && (
                    <RoommatesPage
                        onGoBack={handleGoToYourDetails}
                        onFormSubmit={handleGoToAgreements}
                    />
                )}

                {activeStep === StepEnum.Agreements && (
                    <AgreementsPage
                        hideBackButton={false}
                        onGoBack={handleGoBack}
                        hasRoommates={hasRoommates}
                        onSubmit={handleSignAgreementsAndProgress}
                        loading={loading}
                        error={sagepayError}
                        adminFee={false}
                    />
                )}
            </Box>
            <Box css={styles.rightSide}>
                <RoomCard withSmallImage roomData={roomData} isAgreementsPage={isAgreementsPage} />
                {!occupantFlow && (
                    <Box css={styles.paymentPlanWrapper}>
                        {hasDiscount && (
                            <DiscountBanner>
                                <Typography variant="body3">
                                    {t('discount_applied_info')}
                                </Typography>
                            </DiscountBanner>
                        )}
                        <Box css={styles.overviewPayment}>
                            <PaymentPlan
                                showCta={false}
                                withHeaderStipe={false}
                                currency={currency}
                                disabled={false}
                            />
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    );
};
